import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { HIDDEN_PRICE_DISPLAY_TYPE, HiddenPrice } from '@client/common/components/HiddenPrice/HiddenPrice';
import { Price } from '@client/product/components/ProductData/PriceList/Price';
import { SecondaryPrices } from '@client/product/components/ProductData/PriceList/SecondaryPrices';
import { usePricesTexts } from '@client/product/hooks';
import { useProductBulkPrices } from '@client/product/hooks/useProductBulkPrices';
import { getProductSecondaryPrices } from '@client/product/utils/getProductSecondaryPrices';
import classNames from 'classnames';
import { HydratedContent } from 'b2b-components/core/components/HydratedContent';
import { BasePrice } from 'b2b-components/theme/components/BasePrice';
import { Props } from './index';
import './PriceList.scss';

export const PriceList = ({ effectivePricing, className }: Props): ReactElement => {
  const {
    basePrice,
    priceTaxRate,
    isHiddenPrice,
    isHiddenPriceAndNotCertified,
  } = effectivePricing;
  const text = usePricesTexts(priceTaxRate);
  const prices = getProductSecondaryPrices({ effectivePricing, text });
  const params = useParams<{ sku: string }>();
  const { bulkPrices, bestMatchIndex } = useProductBulkPrices(params.sku);

  const rootClassName = classNames(
    'PriceList',
    {
      'PriceList--WithBasePrice': basePrice,
    },
    className,
  );

  if (isHiddenPrice) {
    return (
      <HiddenPrice
        className="PriceList-HiddenPrice"
        displayType={HIDDEN_PRICE_DISPLAY_TYPE.full}
        isHiddenPriceAndNotCertified={isHiddenPriceAndNotCertified}
      />
    );
  }

  const { price, tax } = prices.userPriceWithoutVat || prices.listPrice;
  const selectedPrice = bulkPrices?.[bestMatchIndex]?.priceWithoutVat ?? price;

  return (
    <HydratedContent>
      <div className={rootClassName}>
        <div className='PriceList-MainPrice'>
          <Price
            price={selectedPrice}
            tax={tax}
            isSecondary={false}
          />
        </div>

        {basePrice && (
          <div className='PriceList-BasePrice'>
            <BasePrice
              unit={basePrice.unit}
              price={basePrice.price}
              currency={'CHF'}
            />
          </div>
        )}

        <SecondaryPrices prices={prices} />
      </div>
    </HydratedContent>
  );
};
